// src/components/messaging/PinMessageButton.tsx

import { useState } from 'react';
import { usePinMessageMutation, useUnpinMessageMutation } from '../../api/threadCollaborationApi';
import { useMessagePermissions } from '../../hooks/useMessagePermissions';

interface PinMessageButtonProps {
  messageId: string;
  workspaceId: string;
  isPinned?: boolean;
  className?: string;
}

export default function PinMessageButton({
  messageId,
  workspaceId,
  isPinned = false,
  className = '',
}: PinMessageButtonProps) {
  const [showDurationMenu, setShowDurationMenu] = useState(false);
  const [pinMessage, { isLoading: isPinning }] = usePinMessageMutation();
  const [unpinMessage, { isLoading: isUnpinning }] = useUnpinMessageMutation();

  // messages.write — pin/unpin is guarded server-side by messages.send.
  // Gated here rather than at each render site so every consumer of this
  // button inherits the rule; a new call site cannot forget it.
  const { canWriteMessages } = useMessagePermissions();

  const handlePin = async (duration: '1h' | '1d' | '1w' | 'forever') => {
    await pinMessage({ messageId, workspaceId, duration });
    setShowDurationMenu(false);
  };

  const handleUnpin = async () => {
    await unpinMessage({ messageId, workspaceId });
  };

  // Pinning is a mutation; a reader simply does not get the affordance.
  if (!canWriteMessages) return null;

  if (isPinned) {
    return (
      <button
        onClick={handleUnpin}
        disabled={isUnpinning}
        className={`p-1 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded transition-colors ${className}`}
        title="Unpin message"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDurationMenu(!showDurationMenu)}
        disabled={isPinning}
        className={`p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors ${className}`}
        title="Pin message"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
          />
        </svg>
      </button>

      {showDurationMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDurationMenu(false)}
          />
          {/* Menu */}
          <div className="absolute bottom-full right-0 mb-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
            <p className="px-3 py-1 text-xs text-gray-500 font-medium">Pin for...</p>
            {[
              { value: '1h', label: '1 hour' },
              { value: '1d', label: '1 day' },
              { value: '1w', label: '1 week' },
              { value: 'forever', label: 'Forever' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handlePin(option.value as '1h' | '1d' | '1w' | 'forever')}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
